/* =========================================================
   Free Shipping Bar — page + drawer (robust updater)
   ========================================================= */

(() => {
  const BAR_SELECTOR = '.gea-free-ship';
  const DEFAULT_THRESHOLD = 19900; // $199.00 in cents
  const MONEY_FMT = (window.Shopify && Shopify.money_format) || "{{amount}}";

  /* ----------------- Money helpers ----------------- */
  const fmt = (cents, format) => {
    try {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, format);
      }
    } catch (e) {}
    return (cents / 100).toFixed(2);
  };

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    return await res.json();
  }

  /* ----------------- Drawer helpers ----------------- */
  const DRAWER_CANDIDATES = [
    '#CartDrawer', '.cart-drawer', 'cart-drawer',
    '[data-cart-drawer]', '.ajaxcart', '.mini-cart',
    '.cart-notification', 'cart-notification', '#CartNotification',
    '[role="dialog"][aria-label*="Cart"]', '[role="dialog"][id*="Cart"]'
  ];

  function findDrawer() {
    for (const sel of DRAWER_CANDIDATES) {
      const el = document.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  function findDrawerFooter(drawer) {
    return (
      drawer.querySelector('.drawer__footer') ||
      drawer.querySelector('.cart-drawer__footer') ||
      drawer.querySelector('.ajaxcart__footer') ||
      drawer.querySelector('.mini-cart__footer') ||
      drawer.querySelector('.cart__ctas') ||
      drawer.querySelector('[data-drawer-footer]') ||
      drawer.querySelector('[data-shopify="cart-subtotal"]') ||
      drawer
    );
  }

  function drawerBarHTML() {
    const mf = String(MONEY_FMT).replace(/"/g, '&quot;');
    return `
<div class="gea-free-ship gea-free-ship--drawer"
     role="region"
     aria-label="Free shipping progress (drawer)"
     data-threshold="${DEFAULT_THRESHOLD}"
     data-money-format="${mf}">
  <div class="gea-free-ship__bar" aria-hidden="false">
    <div class="gea-free-ship__fill" style="width:0%"></div>
    <div class="gea-ms" aria-hidden="true">
      <div class="gea-ms__item" data-key="m2" style="left:96%">
        <span class="gea-ms__circle" aria-hidden="true">
          <svg class="icon--lock" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M4 7V5a4 4 0 118 0v2h1a1 1 0 011 1v6a1 1 0 01-1 1H3a1 1 0 01-1-1V8a1 1 0 011-1h1zm2 0h4V5a2 2 0 10-4 0v2z"/>
          </svg>
          <svg class="icon--tick" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M6.5 11.5l-3-3 1.4-1.4 1.6 1.6 4-4 1.4 1.4-5.4 5.4z"/>
          </svg>
          <span class="gea-ms__title">$199</span>
        </span>
        <span class="gea-ms__label">Free Standard Shipping</span>
      </div>
    </div>
  </div>
  <p class="gea-free-ship__msg">
    <span class="gea-free-ship__text" data-state="progress">
      Spend <span class="gea-amount"></span> more to unlock <strong>FREE shipping</strong>.
    </span>
    <span class="gea-free-ship__text gea-free-ship__text--success" data-state="success" hidden>
      You’ve qualified for <strong>FREE shipping</strong>! 🎉
    </span>
  </p>
</div>`;
  }

  function ensureDrawerBar() {
    const drawer = findDrawer();
    if (!drawer) return;

    if (!drawer.querySelector('.gea-free-ship--drawer')) {
      const host = findDrawerFooter(drawer);
      const wrap = document.createElement('div');
      wrap.innerHTML = drawerBarHTML();
      const bar = wrap.firstElementChild;
      // insert just above subtotal/buttons
      host.parentNode.insertBefore(bar, host);
    }
  }

  /* ----------------- Core updater ----------------- */
  function updateBar(el, cart) {
    const threshold = parseInt(el.dataset.threshold, 10) || DEFAULT_THRESHOLD;
    const moneyFmt  = el.dataset.moneyFormat || MONEY_FMT;
    const total     = cart.total_price || 0;
    const remaining = Math.max(0, threshold - total);
    const pct       = Math.min(100, Math.max(0, (total / threshold) * 100));

    const fill = el.querySelector('.gea-free-ship__fill');
    if (fill) fill.style.width = pct + '%';

    const prog = el.querySelector('.gea-free-ship__text[data-state="progress"]');
    const succ = el.querySelector('.gea-free-ship__text[data-state="success"]');
    if (prog) prog.hidden = remaining <= 0;
    if (succ) succ.hidden = !(remaining <= 0);

    const amt = el.querySelector('.gea-amount');
    if (amt) amt.textContent = fmt(remaining, moneyFmt);

    const m2 = el.querySelector('.gea-ms__item[data-key="m2"]');
    if (m2) m2.classList.toggle('unlocked', remaining <= 0);

    el.classList.toggle('gea-free-ship--qualified', remaining <= 0);
  }

  let _timer = null;
  function triggerUpdate(delay = 120) {
    clearTimeout(_timer);
    _timer = setTimeout(updateAll, delay);
  }

  async function updateAll() {
    // Make sure the drawer has a bar we can update
    ensureDrawerBar();

    const bars = document.querySelectorAll(BAR_SELECTOR);
    if (!bars.length) return;

    let cart;
    try {
      cart = await fetchCart();
    } catch (e) {
      return;
    }

    bars.forEach(el => {
      try { updateBar(el, cart); } catch {}
    });
  }

  /* ----------------- Event wiring ----------------- */

  // 1) Common theme events
  [
    'cart:updated','cart:change','cart:request:complete',
    'ajaxProduct:added','product:added','variant:change',
    'shopify:section:load','cart:refresh'
  ].forEach(evt => document.addEventListener(evt, () => triggerUpdate(60)));

  // 2) Intercept fetch() calls to cart endpoints (apps/themes)
  (function patchFetch() {
    if (!window.fetch || window.__FSB_FETCH_PATCHED__) return;
    const orig = window.fetch;
    window.fetch = function(input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const hitsCart = /\/cart(\/(add|change|update|clear)\.js|\.(add|change|update|clear))(\?|$)/.test(url);
      return orig(input, init).then(res => {
        if (hitsCart) Promise.resolve().then(() => triggerUpdate(80));
        return res;
      });
    };
    window.__FSB_FETCH_PATCHED__ = true;
  })();

  // 3) Intercept legacy XHR cart calls too
  (function patchXHR() {
    if (!window.XMLHttpRequest || window.__FSB_XHR_PATCHED__) return;
    const open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url) {
      this.__fsb_cart_hit = /\/cart(\/(add|change|update|clear)\.js|\.(add|change|update|clear))(\?|$)/.test(url || '');
      return open.apply(this, arguments);
    };
    const send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function() {
      this.addEventListener('load', () => { if (this.__fsb_cart_hit) triggerUpdate(80); }, { once: true });
      return send.apply(this, arguments);
    };
    window.__FSB_XHR_PATCHED__ = true;
  })();

  // 4) Observe DOM so when drawers open/rerender we inject & update
  new MutationObserver(() => triggerUpdate(150))
    .observe(document.documentElement, { childList: true, subtree: true });

  // 5) First run
  triggerUpdate(0);
})();
